import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { Clickable, OnEscapeListener } from '@carvana/merch-components';
import {
  memo,
  useContext,
  createRef,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import FocusLock from 'react-focus-lock';
import Slider from 'react-slick';
import { useSwipeable } from 'react-swipeable';
import LazyDisplayItem from '@/common/LazyDisplayItem';
import GalleryImageType from '~/constants/vdp/galleryImageType';
import { useIsMobile } from '~/context/common/DeviceContext';
import CarouselContext from '~/context/vdp/CarouselContext';
import { useHorizontalScroll, useOptimizeImage } from '~/hooks/common';
import useGalleryImages from '~/hooks/vdp/useGalleryImages';
import Chevron from '~/icons/Chevron';
import Close from '~/icons/Close';
import { EventName, GalleryType } from './constants';
import styles from './FullScreenGallery.module.css';
import {
  CarouselWrapper,
  ExternalSideBarNav,
  FullScreenWrapper,
  ImageTitle,
  NextNavButton,
  PrevNavButton,
  SideBarImage,
  type SideBarImageProps,
  SliderImageWrapper,
  SliderWrapper
} from './FullScreenGallery.styles';

const scrollPreviewIntoView = (ref: React.RefObject<HTMLElement>) => {
  if (typeof ref?.current?.scrollIntoView === 'function') {
    ref.current.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  }
};

export default memo(function FullScreenGallery() {
  const images = useGalleryImages();
  const optimizeImage = useOptimizeImage();
  const { closeFullScreen, setSlideImgId, slideImgId } =
    useContext(CarouselContext);
  const isMobile = useIsMobile();
  const sliderRef = useRef<Slider>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const sidebarNavRef = useHorizontalScroll();

  const totalImages = images.length;
  const showButtons = totalImages > 1 && !isMobile;
  const thumbnailRefs = useMemo(
    () =>
      [...Array(totalImages)].map<React.RefObject<HTMLButtonElement>>(() =>
        createRef()
      ),
    [totalImages]
  );

  const { trackEvent } = useContext(AnalyticsContext);

  const slideIndex = (index => (index === -1 ? 0 : index))(
    images.findIndex(({ id }) => id === slideImgId)
  );

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      trackEvent('VDP - Gallery - Swiped Left on Full Screen Carousel');
    },
    onSwipedRight: () => {
      trackEvent('VDP - Gallery - Swiped Right on Full Screen Carousel');
    },
    trackTouch: true,
    trackMouse: true
  });

  // Scroll preview into view when component loads
  useEffect(() => {
    const thumbnailRef = thumbnailRefs[slideIndex];
    const t = setTimeout(() => scrollPreviewIntoView(thumbnailRef), 500);

    return () => clearTimeout(t);
  }, [slideIndex, thumbnailRefs]);

  useEffect(() => {
    scrollPreviewIntoView(thumbnailRefs[slideIndex]);
  }, [thumbnailRefs, slideIndex]);

  useEffect(() => {
    const isImperfectionVideo = Boolean(images[slideIndex]?.videoUrl);
    const isSafetyFeature =
      images[slideIndex]?.type === GalleryImageType.SafetyFeature;
    if (isSafetyFeature) {
      trackEvent('VDP - Gallery - Viewed Safety Feature Imagery');
    }
    if (isImperfectionVideo) {
      trackEvent('VDP - Gallery - Viewed Imperfection Video');
    }
  }, [images, slideIndex, trackEvent]);

  // Call slickGoTo method only when slide id is changed by context method
  // and not when slider updates slide id in afterChange callback to prevent endless loop
  useEffect(() => {
    if (currentIndex !== slideIndex) {
      sliderRef?.current?.slickGoTo(slideIndex);
    }
  }, [currentIndex, slideIndex]);

  // Focus on slider mount to make navigation accessible on arrow press
  useEffect(() => {
    sliderRef?.current?.innerSlider?.list?.setAttribute('tabindex', '-1');
    sliderRef?.current?.innerSlider?.list?.focus();
  }, [sliderRef]);

  return (
    <FocusLock autoFocus={false}>
      <OnEscapeListener onEscape={closeFullScreen}>
        <FullScreenWrapper>
          <Clickable
            className="z-1 justify-self-end p-16 text-white"
            onClick={closeFullScreen}
            title="Close"
            data-analytics-id={null}
          >
            <Close width={32} height={32} />
          </Clickable>

          <CarouselWrapper {...swipeHandlers}>
            <SliderWrapper
              className={styles.sliderWrapper}
              onClick={e => {
                if (
                  e.target !==
                  sliderRef.current?.innerSlider?.list?.querySelector(
                    '.slick-current img'
                  )
                ) {
                  closeFullScreen();
                }
              }}
              data-analytics-id={null}
            >
              <Slider
                afterChange={index => {
                  const currentImage = images[index];
                  setCurrentIndex(index);
                  setSlideImgId(currentImage?.id);
                  trackEvent(EventName.galleryEngaged, {
                    galleryType: GalleryType.FullScreen,
                    imageType: currentImage?.type
                  });
                }}
                beforeChange={index => {
                  setCurrentIndex(index);
                }}
                arrows={false}
                dots={false}
                ref={sliderRef}
                slidesToScroll={1}
                slidesToShow={1}
                speed={300}
              >
                {images.map(
                  (
                    { imageUrl, title, type, videoUrl, zoneDescription },
                    index
                  ) => {
                    const optimizedImage = optimizeImage(
                      imageUrl,
                      isMobile ? 768 : 2000,
                      { crop: isMobile ? '4:3' : undefined }
                    );
                    const showTitle =
                      Boolean(title) &&
                      [
                        GalleryImageType.Feature,
                        GalleryImageType.Imperfection
                      ].includes(type);

                    if (!optimizedImage) return null;

                    return (
                      <SliderImageWrapper key={optimizedImage}>
                        <LazyDisplayItem
                          offsetToLoad={4}
                          totalItems={totalImages}
                          visibleSlideIndex={slideIndex}
                          indexToDisplay={index}
                        >
                          {videoUrl ? (
                            <video
                              className="col-span-3 mx-auto h-full object-contain"
                              autoPlay
                              loop
                              muted
                              playsInline
                              poster={optimizedImage}
                            >
                              <source src={videoUrl} type="video/mp4" />
                              This browser does not support the HTML5 video
                              element.
                            </video>
                          ) : (
                            <img
                              className="col-span-3 mx-auto h-full object-contain"
                              alt="A vehicle"
                              src={optimizedImage}
                              onTouchStart={e => {
                                // this allows both pinch zoom and swiping
                                if (e.touches.length > 1) {
                                  e.stopPropagation();
                                }
                              }}
                            />
                          )}

                          <span className="pl-8 m:pl-24">
                            {index + 1} of {totalImages}
                          </span>

                          {showTitle && (
                            <ImageTitle>
                              <span>
                                {title}
                                {zoneDescription ? ` (${zoneDescription})` : ''}
                              </span>
                            </ImageTitle>
                          )}
                        </LazyDisplayItem>
                      </SliderImageWrapper>
                    );
                  }
                )}
              </Slider>
            </SliderWrapper>

            {showButtons && (
              <Clickable
                data-analytics-id={`Full Screen Gallery - Previous`}
                onClick={() => sliderRef?.current?.slickPrev()}
                element={PrevNavButton}
              >
                <Chevron reversed />
              </Clickable>
            )}

            {showButtons && (
              <Clickable
                data-analytics-id={`Full Screen Gallery - Next`}
                onClick={() => {
                  sliderRef?.current?.slickNext();
                }}
                element={NextNavButton}
              >
                <Chevron />
              </Clickable>
            )}
          </CarouselWrapper>

          <ExternalSideBarNav ref={sidebarNavRef}>
            {images.map(({ imageUrl, id }, index) => {
              const optimizedImage = optimizeImage(imageUrl, 100);

              return optimizedImage ? (
                <Clickable<SideBarImageProps>
                  key={optimizedImage}
                  element={SideBarImage}
                  elementRef={thumbnailRefs[index]}
                  onClick={() => setSlideImgId(id)}
                  data-analytics-id="Gallery - Nav Image"
                  data-selected={slideIndex === index}
                  selected={slideIndex === index}
                  style={{ '--cv-image-url': `url(${optimizedImage})` }}
                />
              ) : null;
            })}
          </ExternalSideBarNav>
        </FullScreenWrapper>
      </OnEscapeListener>
    </FocusLock>
  );
});
